// Враги и лут.
//
// ЧЕСТНОСТЬ ЗДЕСЬ ВАЖНЕЕ ПОЛНОТЫ. Три факта состояния проекта стоят
// первыми и написаны словами, а не выведены из таблицы:
//   1. Дроп с трупов ЕСТЬ (с 2026-09-07). При удаче падает ровно один
//      предмет, выбранный по весам таблицы, и ложится на пол обычной
//      находкой. До этого дня страница утверждала обратное — и была
//      права; теперь это самое важное, что на ней изменилось.
//   2. Что уронит конкретный враг, решается ПРИ ГЕНЕРАЦИИ уровня, а не
//      в момент убийства. Иначе результат зависел бы от порядка убийств
//      и забег перестал бы воспроизводиться по seed.
//   3. Ассетов EnemyDef в проекте по-прежнему ноль: работает запасной
//      путь в коде корня. Это надо продолжать говорить словами —
//      пустая таблица на этом месте читается как «данные не
//      загрузились», и человек лезет чинить загрузку вместо того,
//      чтобы завести врага.
//
// ГЛАВНОЕ ЧИСЛО СТРАНИЦЫ — не состав таблицы дропа, а то, что дроп
// делает с дефицитом патронов: патроны с трупов это патроны на уровне,
// и первый столп игры стоит ровно на них.

import { useEffect, useMemo, useState } from 'react';
import { useStore } from '../data/store';
import { loadCatalog, loadSpawnStats } from '../data/source';
import { Callout, OriginTag, Panel, Tile } from '../components/Ui';
import { BoxPlots } from '../components/Charts';
import type { SpawnStatistics } from '../lib/spawns';
import {
  MEASURED_CURVE_RUNS,
  MEASURED_CURVE_TABLE,
  MEASURED_DEFICIT_CURVE,
  countDropRows,
  dropTableRows,
  expectedItemsPerKill,
  hasDropData,
  nearestMeasured,
  summarizeDrops,
} from '../lib/drops';
import { num, percent, signed } from '../lib/format';
import type { Catalog, DropsConfig } from '../lib/types';

const SOURCE_LABELS: Record<string, string> = {
  compositionRootFallback: 'запасные значения в CompositionRoot',
  simPrototype: 'прототип внутри симуляции',
};

const ROOM_KIND_LABELS: Record<string, string> = {
  Entrance: 'Вход',
  Exit: 'Выход',
  SaveRoom: 'Сейв-комната',
  Ordinary: 'Обычная',
};

const LOOT_COLORS: Record<string, string> = {
  Ammo: 'var(--series-c)',
  Medkit: 'var(--series-a)',
  Key: 'var(--series-b)',
  Battery: 'var(--series-e)',
  Talons: 'var(--series-d)',
};

const DROP_TABLE_SOURCE_LABELS: Record<string, string> = {
  compositionRootFallback: 'запасная таблица в CompositionRoot',
  none: 'таблицы нет',
};

export function Bestiary() {
  const store = useStore();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [spawns, setSpawns] = useState<SpawnStatistics | null>(null);
  const [spawnOrigin, setSpawnOrigin] = useState<'server' | 'sample'>('sample');
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    const server = store.server;
    if (server === null) return;
    let alive = true;
    void (async () => {
      try {
        const result = await loadCatalog(server.online);
        if (alive) setCatalog(result.value);
      } catch (error) {
        if (alive) setFailure(error instanceof Error ? error.message : String(error));
      }
    })();
    return () => {
      alive = false;
    };
  }, [store.server]);

  useEffect(() => {
    const server = store.server;
    const dataset = store.dataset;
    if (server === null || dataset === null) return;
    let alive = true;
    void (async () => {
      const result = await loadSpawnStats(server.online, dataset);
      if (!alive) return;
      setSpawns(result.value);
      setSpawnOrigin(result.origin);
    })();
    return () => {
      alive = false;
    };
  }, [store.server, store.dataset]);

  const maxEnemiesPerRoom = useMemo(
    () => (spawns ? Math.max(0.0001, ...spawns.byDistance.map((b) => b.enemiesPerRoom)) : 1),
    [spawns],
  );
  const maxLootPerRoom = useMemo(
    () => (spawns ? Math.max(0.0001, ...spawns.byDistance.map((b) => b.lootPerRoom)) : 1),
    [spawns],
  );

  return (
    <div className="page">
      <div className="page-head">
        <h1>Враги и лут</h1>
        <p>
          Каталог существ, статистика их появления по комнатам и лут-таблицы. Данные о появлении
          берутся из строк подробностей (раздел 1b контракта) — того же прогона, что и числа на
          остальных страницах.
        </p>
        {catalog && <OriginTag origin={catalog.origin} />}
      </div>

      <Callout
        tone={catalog === null || catalog.dropsLoot ? 'info' : 'hard'}
        title={
          catalog === null
            ? 'Каталог ещё не загружен'
            : catalog.dropsLoot
              ? 'Мобы в этой игре роняют лут'
              : 'Каталог сообщает, что дропа нет'
        }
      >
        {catalog !== null && catalog.dropsLoot ? (
          <>
            <p>
              <strong>При удаче с трупа падает ровно один предмет</strong>, выбранный по весам
              таблицы, и ложится на пол обычной находкой. Не набор, не «что-нибудь ещё сверху» —
              один предмет или ничего.
            </p>
            <p>
              <strong>Что уронит конкретный враг, решается при генерации уровня</strong>, а не в
              момент убийства. Иначе результат зависел бы от порядка убийств, и забег перестал бы
              воспроизводиться по seed — а на воспроизводимости стоит всё сравнение конфигов в этом
              приложении.
            </p>
            <p>
              <strong>Ассетов EnemyDef в проекте по-прежнему ноль.</strong>{' '}
              {`Каталог нашёл ${num(catalog.assetsFound)} ассетов EnemyDef.`} И шанс, и таблица
              приезжают из запасного пути в коде композиционного корня, а не из{' '}
              <span className="mono">Data/</span>. Всё, что ниже, — параметры одного существа,
              собранного кодом, а не бестиарий игры.
            </p>
            <p>
              Лут в комнатах расставляет <span className="mono">LevelPopulator</span>, и с дропом он
              не связан: это два разных источника предметов на уровне, и складывать их в одну
              таблицу нельзя.
            </p>
            {catalog.dropsNote && (
              <p className="dim" style={{ marginTop: 8 }}>
                Дословно от каталога: {catalog.dropsNote}
              </p>
            )}
          </>
        ) : catalog === null ? (
          <p>Пока каталог не пришёл, сказать про дроп нечего — а гадать здесь нельзя.</p>
        ) : (
          <>
            <p>
              Каталог ответил <span className="mono">dropsLoot: false</span>. Это расходится с
              контрактом схемы 2, где дроп уже есть: либо каталог собран старым{' '}
              <span className="mono">spusk-sim</span>, либо образец испечён до появления механики.
              Перепеките образец (<span className="mono">npm run bake</span>) или поднимите сервер.
            </p>
            <p>
              <strong>Ассетов EnemyDef в проекте ноль.</strong>{' '}
              {`Каталог нашёл ${num(catalog.assetsFound)} ассетов EnemyDef.`}
            </p>
          </>
        )}
      </Callout>

      <DropPanels catalog={catalog} />

      {failure && (
        <Callout tone="hard" title="Каталог не прочитался">
          <p className="mono">{failure}</p>
        </Callout>
      )}

      <Panel title="Каталог врагов" flush>
        {catalog === null ? (
          <div className="empty">загрузка…</div>
        ) : catalog.enemies.length === 0 ? (
          <div className="empty">каталог пуст — ни одного существа не собралось даже из кода</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>id</th>
                <th>Откуда число</th>
                <th className="n">здоровье</th>
                <th className="n">шаг, м/с</th>
                <th className="n">бег, м/с</th>
                <th className="n">присед, м/с</th>
                <th className="n">поглощение</th>
                <th className="n">урон</th>
                <th className="n">дальность, м</th>
                <th className="n">перезарядка, с</th>
                <th className="n">шанс дропа</th>
                <th>Таблица дропа</th>
              </tr>
            </thead>
            <tbody>
              {catalog.enemies.map((enemy, index) => (
                <tr key={`${enemy.id}-${enemy.source}-${index}`}>
                  <td className="mono">{enemy.id}</td>
                  <td>
                    <span className="tag warn">{SOURCE_LABELS[enemy.source] ?? enemy.source}</span>
                  </td>
                  <td className="n">{num(enemy.maxHealth)}</td>
                  <td className="n">{num(enemy.walkSpeed, 2)}</td>
                  <td className="n">{num(enemy.runSpeed, 2)}</td>
                  <td className="n">{num(enemy.crouchSpeed, 2)}</td>
                  <td className="n">{num(enemy.damageAbsorptionPercent)}%</td>
                  <td className="n">{num(enemy.meleeDamage)}</td>
                  <td className="n">{num(enemy.meleeRange, 2)}</td>
                  <td className="n" title="восстановлено из тиков 30 Гц: округлённое, а не набранное в инспекторе">
                    {num(enemy.meleeCooldownSeconds, 3)}*
                  </td>
                  <td className={enemy.dropChancePercent === undefined ? 'n faint' : 'n warn'}>
                    {enemy.dropChancePercent === undefined
                      ? '—'
                      : `${num(enemy.dropChancePercent)}%`}
                  </td>
                  <td>
                    {enemy.dropTableSource === undefined ? (
                      <span className="faint">каталог схемы 1: поля нет</span>
                    ) : (
                      <span className={enemy.dropTableSource === 'none' ? 'tag bad' : 'tag warn'}>
                        {DROP_TABLE_SOURCE_LABELS[enemy.dropTableSource] ?? enemy.dropTableSource}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {catalog && catalog.enemies.length > 0 && (
        <p className="dim" style={{ marginTop: -6, marginBottom: 14 }}>
          * <span className="mono">meleeCooldownSeconds</span> восстановлен из тиков при 30 Гц: показано
          то, чем число стало после округления, а не то, что набрано в инспекторе. Строки с разным{' '}
          <span className="mono">source</span> — один и тот же зомби, увиденный с двух сторон; пока они
          совпадают, но разойдутся — это будет видно прямо здесь.
        </p>
      )}

      <Panel
        title="Где появляются враги и лут"
        aside={
          <div className="controls">
            <span className="dim">
              датасет <span className="mono">{store.dataset ?? '—'}</span>
              {spawns && <> · {num(spawns.seeds)} seed · {num(spawns.rooms)} комнат</>}
            </span>
            <OriginTag origin={spawnOrigin} />
          </div>
        }
      >
        {spawns === null ? (
          <Callout tone="hard" title="Строк подробностей для этого датасета нет">
            <p>
              Разбивка по комнатам живёт в отдельном файле <span className="mono">*-detail.jsonl</span>{' '}
              (раздел 1b контракта). Строка прогона хранит только итоги уровня, и вывести из них, в каких
              комнатах стояли враги, невозможно. Прогоните <span className="mono">spusk-sim balance --detail</span> для
              этого конфига или выберите датасет, у которого файл подробностей есть.
            </p>
          </Callout>
        ) : (
          <>
            <div className="tiles">
              <Tile
                label="Врагов в комнате входа"
                value={num(spawns.enemiesInEntrance)}
                tone={spawns.enemiesInEntrance === 0 ? 'good' : 'bad'}
                note="отступ от входа обязан держать её пустой"
              />
              <Tile
                label="Врагов в комнате выхода"
                value={num(spawns.enemiesInExit)}
                tone={spawns.enemiesInExit === 0 ? 'good' : 'bad'}
                note="то же для выхода"
              />
              <Tile
                label="Недостижимых комнат"
                value={num(spawns.unreachableRooms)}
                tone={spawns.unreachableRooms === 0 ? 'good' : 'bad'}
                note={
                  spawns.unreachableRooms === 0
                    ? 'связность держится на всех seed'
                    : `seed: ${spawns.unreachableSeeds.slice(0, 8).join(', ')}${spawns.unreachableSeeds.length > 8 ? '…' : ''}`
                }
              />
              <Tile
                label="Глубина уровня"
                value={num(spawns.maxDistance)}
                unit="рёбер"
                note="максимум расстояния от входа по графу комнат"
              />
            </div>

            {spawns.factsNote && (
              <p className="dim" style={{ marginTop: 0 }}>
                {spawns.factsNote}
              </p>
            )}

            <h3 style={{ fontSize: 'var(--fs-sm)', margin: '4px 0 8px' }}>
              Врагов и лута по расстоянию от входа
            </h3>
            <div className="scroll" style={{ maxHeight: '42vh' }}>
              <table>
                <thead>
                  <tr>
                    <th className="n">рёбер от входа</th>
                    <th className="n">комнат</th>
                    <th className="n">врагов</th>
                    <th className="n">на комнату</th>
                    <th style={{ width: '22%' }}>плотность врагов</th>
                    <th className="n">находок</th>
                    <th className="n">на комнату</th>
                    <th style={{ width: '22%' }}>плотность лута</th>
                  </tr>
                </thead>
                <tbody>
                  {spawns.byDistance.map((band) => (
                    <tr key={band.distance}>
                      <td className="n mono">{band.distance}</td>
                      <td className="n dim">{num(band.rooms)}</td>
                      <td className="n">{num(band.enemies)}</td>
                      <td className="n good">{num(band.enemiesPerRoom, 2)}</td>
                      <td>
                        <Bar value={band.enemiesPerRoom / maxEnemiesPerRoom} color="var(--series-a)" />
                      </td>
                      <td className="n">{num(band.lootPlacements)}</td>
                      <td className="n warn">{num(band.lootPerRoom, 2)}</td>
                      <td>
                        <Bar value={band.lootPerRoom / maxLootPerRoom} color="var(--series-c)" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="split" style={{ marginTop: 14 }}>
              <div>
                <h3 style={{ fontSize: 'var(--fs-sm)', margin: '0 0 8px' }}>По виду комнаты</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Вид</th>
                      <th className="n">комнат</th>
                      <th className="n">врагов/комн</th>
                      <th className="n">с врагом</th>
                      <th className="n">находок/комн</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spawns.byKind.map((group) => (
                      <tr key={group.key}>
                        <td>{ROOM_KIND_LABELS[group.key] ?? group.key}</td>
                        <td className="n dim">{num(group.rooms)}</td>
                        <td className="n good">{num(group.enemiesPerRoom, 2)}</td>
                        <td className="n">{percent(group.roomsWithEnemyShare)}</td>
                        <td className="n warn">{num(group.lootPerRoom, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <h3 style={{ fontSize: 'var(--fs-sm)', margin: '0 0 8px' }}>По размеру комнаты</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Класс</th>
                      <th className="n">комнат</th>
                      <th className="n">клеток/комн</th>
                      <th className="n">врагов/комн</th>
                      <th className="n">находок/комн</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spawns.bySize.map((group) => (
                      <tr key={group.key}>
                        <td>{group.key}</td>
                        <td className="n dim">{num(group.rooms)}</td>
                        <td className="n dim">{num(group.cells / Math.max(1, group.rooms), 1)}</td>
                        <td className="n good">{num(group.enemiesPerRoom, 2)}</td>
                        <td className="n warn">{num(group.lootPerRoom, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <h3 style={{ fontSize: 'var(--fs-sm)', margin: '14px 0 8px' }}>Что именно расставлено</h3>
            <table>
              <thead>
                <tr>
                  <th>Вид находки</th>
                  <th className="n">размещений</th>
                  <th className="n">штук всего</th>
                  <th className="n">на размещение</th>
                  <th className="n">доля объёма</th>
                </tr>
              </thead>
              <tbody>
                {spawns.lootByKind.map((entry) => (
                  <tr key={entry.kind}>
                    <td>
                      <span className="tag" style={{ color: LOOT_COLORS[entry.kind] ?? undefined }}>
                        {entry.kind}
                      </span>
                    </td>
                    <td className="n">{num(entry.placements)}</td>
                    <td className="n">{num(entry.amount)}</td>
                    <td className="n">{num(entry.amount / Math.max(1, entry.placements), 2)}</td>
                    <td className="n dim">{percent(entry.share)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="dim" style={{ marginBottom: 0, marginTop: 8 }}>
              Одна запись — одно размещение, а не сумма по виду: две пачки по три и одна по шесть —
              разная добыча при одинаковой сумме.
            </p>
          </>
        )}
      </Panel>

      <Panel
        title="Лут-таблицы комнат"
        aside={
          catalog && (
            <span className="dim">
              {catalog.lootFromServer
                ? 'прочитано сервером из .asset'
                : 'запечено разбором .asset в public/offline'}
            </span>
          )
        }
      >
        {catalog !== null && catalog.warnings.length > 0 && (
          <Callout tone="hard" title="Замечания к содержимому таблиц">
            {catalog.warnings.map((warning) => (
              <p key={warning}>{warning}</p>
            ))}
          </Callout>
        )}
        {catalog === null ? (
          <div className="empty">загрузка…</div>
        ) : catalog.lootTables.length === 0 ? (
          <Callout tone="hard" title="Лут-таблицы недоступны">
            <p>
              Таблицы лежат в <span className="mono">Assets/_Project/Data/LootTables/*.asset</span> и
              читаются разбором YAML со ссылками по GUID. Это делает сервер; без него список пуст —
              и это отсутствие сервера, а не отсутствие таблиц. Поднимите сервер на 127.0.0.1:5177 либо
              перепеките образец командой <span className="mono">npm run bake</span>.
            </p>
          </Callout>
        ) : (
          catalog.lootTables.map((table) => (
            <div key={table.assetPath} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <strong className="mono">{table.assetName}</strong>
                {table.stub && <span className="tag warn">заглушка</span>}
                <span className="faint mono" style={{ fontSize: 'var(--fs-micro)' }}>
                  {table.assetPath}
                </span>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Предмет</th>
                    <th>id</th>
                    <th className="n">вес</th>
                    <th className="n">шанс</th>
                    <th>Замечания</th>
                  </tr>
                </thead>
                <tbody>
                  {table.entries.map((entry) => (
                    <tr key={entry.guid}>
                      <td>{entry.itemName ?? <span className="bad">ссылка не разрешилась</span>}</td>
                      <td className="mono dim">{entry.itemId ?? entry.guid.slice(0, 8)}</td>
                      <td className="n">{num(entry.weight)}</td>
                      <td className="n good">{percent(entry.share)}</td>
                      <td>
                        {entry.missing && <span className="tag bad">предмет не найден по GUID</span>}
                        {entry.stub && <span className="tag warn">тестовый или заглушечный предмет</span>}
                        {!entry.missing && !entry.stub && <span className="faint">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </Panel>

      <Panel title="Предметы" flush>
        {catalog === null || catalog.items.length === 0 ? (
          <div className="empty">список предметов пуст</div>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>id</th>
                  <th>Название</th>
                  <th>Вид</th>
                  <th className="n">стек</th>
                  <th className="n">клеток</th>
                  <th className="n">граммов</th>
                  <th className="n">лечит</th>
                  <th>Замечания</th>
                </tr>
              </thead>
              <tbody>
                {catalog.items.map((item) => (
                  <tr key={item.assetPath}>
                    <td className="mono">{item.id ?? '—'}</td>
                    <td>{item.displayName ?? item.assetName}</td>
                    <td className="dim">{item.kind}</td>
                    <td className="n">{item.stackable ? num(item.stackLimit) : '—'}</td>
                    <td className="n dim">
                      {item.width}×{item.height}
                    </td>
                    <td className="n dim">{num(item.weightGrams)}</td>
                    <td className="n">{item.healAmount > 0 ? num(item.healAmount) : '—'}</td>
                    <td>
                      {item.stub && <span className="tag warn">заглушка</span>}
                      {item.isInfected && <span className="tag bad">заражён</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

/**
 * Дроп: таблица с шансами и то, что дроп делает с дефицитом патронов.
 *
 * ДВЕ ПАНЕЛИ, А НЕ ОДНА, потому что это два разных вопроса. Первая
 * отвечает «что и с какой вероятностью падает» — она про настройку.
 * Вторая отвечает «что от этого происходит с первым столпом игры» — она
 * про последствие, и именно ради неё страница и существует.
 */
function DropPanels({ catalog }: { catalog: Catalog | null }) {
  const store = useStore();

  // Таблица дропа живёт в пресете, а не в каталоге: каталог отдаёт шанс
  // и то, откуда таблица взялась, но не её состав. Берём пресет того же
  // конфига, что и выбранный датасет, иначе — первый, у которого секция
  // дропа вообще есть, и честно говорим, чей это состав.
  const datasetConfigName = useMemo(
    () => store.datasets.find((entry) => entry.name === store.dataset)?.configName ?? null,
    [store.datasets, store.dataset],
  );
  const preset = useMemo(() => {
    const matching = store.presets.find(
      (entry) => entry.config.name === datasetConfigName && entry.config.drops !== undefined,
    );
    return matching ?? store.presets.find((entry) => entry.config.drops !== undefined) ?? null;
  }, [store.presets, datasetConfigName]);

  const drops: DropsConfig | undefined = preset?.config.drops;
  const tableRows = useMemo(() => dropTableRows(drops), [drops]);
  const perKill = expectedItemsPerKill(drops);
  const presetMatches = preset !== null && preset.config.name === datasetConfigName;

  const dropRows = useMemo(() => countDropRows(store.active), [store.active]);
  const summary = useMemo(() => summarizeDrops(store.active), [store.active]);
  const withDrops = useMemo(() => store.active.filter(hasDropData), [store.active]);
  // Мемоизация обязательна: по новому массиву график пересобирается
  // целиком на каждую перерисовку страницы (см. PlotFigure).
  const marginBoxes = useMemo(
    () => [
      {
        label: 'без патронов с трупов',
        values: withDrops.map((row) => row.economy.ammoMargin),
        color: 'var(--series-b)',
      },
      {
        label: 'с патронами с трупов',
        values: withDrops.map((row) => row.economy.ammoMarginWithDrops ?? 0),
        color: 'var(--series-c)',
      },
    ],
    [withDrops],
  );

  const chance = catalog?.enemies.find((e) => e.dropChancePercent !== undefined)?.dropChancePercent;
  const measured = chance === undefined ? null : nearestMeasured(chance);

  // Датасет схемы 2 в списке — чтобы предложить переключиться одним
  // щелчком, а не «найдите сами». Ищем по имени: какой именно датасет
  // несёт дроп, известно только после его загрузки, и грузить их все
  // ради подсказки — дороже, чем показать кнопку.
  const dropsDataset = useMemo(
    () => store.datasets.find((entry) => /drop/i.test(entry.name) && entry.name !== store.dataset),
    [store.datasets, store.dataset],
  );

  return (
    <>
      <Panel
        title="Дроп с врагов: таблица и шансы"
        aside={
          preset === null ? (
            <span className="tag bad">пресета с таблицей дропа нет</span>
          ) : (
            <span className="dim">
              состав таблицы из пресета <span className="mono">{preset.file}</span>
              {!presetMatches && <span className="warn"> — это не конфиг выбранного датасета</span>}
            </span>
          )
        }
      >
        <div className="tiles">
          <Tile
            label="Шанс дропа с трупа"
            value={chance === undefined ? '—' : num(chance)}
            unit={chance === undefined ? undefined : '%'}
            tone={chance === undefined ? 'plain' : 'warn'}
            note={
              chance === undefined
                ? 'каталог схемы 1 этого поля не отдаёт'
                : 'вероятность, что розыгрыш вообще состоится'
            }
          />
          <Tile
            label="Предметов с трупа"
            value="1"
            note="при удаче падает ровно один — не набор"
          />
          <Tile
            label="Ожидаемо предметов на смерть"
            value={num(perKill, 3)}
            tone={perKill > 0 ? 'warn' : 'plain'}
            note="шанс × доля непустых строк таблицы: столько добавит один убитый враг"
          />
          <Tile
            label="Ассетов EnemyDef"
            value={num(catalog?.assetsFound ?? Number.NaN)}
            tone={(catalog?.assetsFound ?? 0) === 0 ? 'warn' : 'good'}
            note="ноль — шанс и таблица приходят из запасного пути в коде"
          />
        </div>

        {tableRows.length === 0 ? (
          <Callout tone="hard" title="Состава таблицы дропа взять неоткуда">
            <p>
              Каталог отдаёт шанс и источник таблицы, но не её состав; состав лежит в секции{' '}
              <span className="mono">drops</span> пресета. Ни у одного загруженного пресета этой
              секции нет. Добавьте пресет с таблицей дропа в{' '}
              <span className="mono">data/presets/</span> и перепеките образец.
            </p>
          </Callout>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Что падает</th>
                  <th className="n">штук</th>
                  <th className="n">вес</th>
                  <th className="n">доля в таблице</th>
                  <th style={{ width: '26%' }}>вес</th>
                  <th className="n">шанс за одну смерть</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr key={`${row.kind}-${row.amount}-${row.weight}`}>
                    <td>
                      {row.empty ? (
                        <span className="tag">пусто — розыгрыш не дал ничего</span>
                      ) : (
                        <span
                          className="tag"
                          style={{ color: LOOT_COLORS[row.kind] ?? undefined }}
                        >
                          {row.kind}
                        </span>
                      )}
                    </td>
                    <td className="n dim">{row.empty ? '—' : num(row.amount)}</td>
                    <td className="n">{num(row.weight)}</td>
                    <td className="n">{percent(row.share)}</td>
                    <td>
                      <Bar
                        value={row.share}
                        color={row.empty ? 'var(--chart-mark-weak)' : (LOOT_COLORS[row.kind] ?? 'var(--series-a)')}
                      />
                    </td>
                    <td className={row.empty ? 'n faint' : 'n good'}>{percent(row.perKill, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="dim" style={{ marginBottom: 0, marginTop: 8 }}>
              «Пусто» — такая же строка таблицы, как остальные, и её вес виден намеренно: без него
              шанс дропа читается как шанс получить лут, а это разные числа. При шансе{' '}
              {chance === undefined ? '—' : `${num(chance)}%`} и этой таблице один убитый враг даёт{' '}
              <b className="warn">{num(perKill, 3)}</b> предмета в среднем.
            </p>
          </>
        )}
      </Panel>

      <Panel
        title="Что дроп делает с дефицитом патронов"
        aside={
          <span className="dim">
            датасет <span className="mono">{store.dataset ?? '—'}</span> ·{' '}
            {num(dropRows)} из {num(store.active.length)} строк со схемой 2
          </span>
        }
      >
        {dropRows === 0 ? (
          <Callout tone="hard" title="В выбранном датасете блока drops нет">
            <p>
              Строки этого датасета написаны схемой 1: в них нет ни блока{' '}
              <span className="mono">drops</span>, ни полей{' '}
              <span className="mono">ammoMarginWithDrops</span> и{' '}
              <span className="mono">respectsAmmoDeficitWithDrops</span>. Подставить сюда нули
              нельзя: ноль патронов с трупов и отсутствие механики дропа выглядят одинаково, а это
              разные вещи.
              {dropsDataset && (
                <>
                  {' '}
                  Датасет со схемой 2 в списке есть.{' '}
                  <button className="action" onClick={() => store.setDataset(dropsDataset.name)}>
                    показать {dropsDataset.name}
                  </button>
                </>
              )}
            </p>
          </Callout>
        ) : (
          <>
            <div className="tiles">
              <Tile
                label="Дефицит держится без дропа"
                value={percent(summary.deficitHeld)}
                tone={summary.deficitHeld >= 1 ? 'good' : 'warn'}
                note="патронов НА ПОЛУ меньше, чем нужно жадному профилю"
              />
              <Tile
                label="Дефицит держится с дропом"
                value={percent(summary.deficitHeldWithDrops)}
                tone={
                  summary.deficitHeldWithDrops >= 0.95
                    ? 'good'
                    : summary.deficitHeldWithDrops >= 0.8
                      ? 'warn'
                      : 'bad'
                }
                note="патроны с трупов посчитаны: столько будет у того, кто убьёт всех"
              />
              <Tile
                label="Дроп ломает дефицит"
                value={percent(summary.brokenByDrops)}
                tone={
                  summary.brokenByDrops === 0
                    ? 'good'
                    : summary.brokenByDrops < 0.1
                      ? 'warn'
                      : 'bad'
                }
                note={`флаг drops_break_deficit: ${num(
                  Math.round(summary.brokenByDrops * summary.dropRows),
                )} из ${num(summary.dropRows)} уровней`}
              />
              <Tile
                label="Патронов с трупов, медиана"
                value={num(summary.ammoFromDrops.p50)}
                note={`p10 ${num(summary.ammoFromDrops.p10)} · p90 ${num(summary.ammoFromDrops.p90)}`}
              />
              <Tile
                label="Запас без дропа, медиана"
                value={signed(summary.ammoMargin.p50)}
                tone={summary.ammoMargin.p50 < 0 ? 'good' : 'bad'}
                note="отрицательное — норма: игрок беднее, чем ему хочется"
              />
              <Tile
                label="Запас с дропом, медиана"
                value={signed(summary.ammoMarginWithDrops.p50)}
                tone={summary.ammoMarginWithDrops.p50 < 0 ? 'good' : 'bad'}
                note="ноль и выше — столп «страх через дефицит» на этом уровне сломан"
              />
            </div>

            <h3 style={{ fontSize: 'var(--fs-sm)', margin: '4px 0 8px' }}>
              Запас патронов: тот же уровень, посчитанный дважды
            </h3>
            <BoxPlots unit="шт" zeroLine rows={marginBoxes} />
            <p className="dim" style={{ marginTop: 0 }}>
              Янтарная риска — ноль. Всё, что от неё справа, это уровень, на котором игрок в итоге
              богаче, чем ему хочется. Обе коробки посчитаны по одним и тем же{' '}
              {num(summary.dropRows)} seed, поэтому расстояние между ними — это ровно дроп, а не
              разница выборок.
            </p>

            {summary.byKind.length === 0 ? (
              <Callout tone="info" title="В этой выборке с трупов не упало ничего">
                <p>
                  Блок <span className="mono">drops</span> в строках есть, а добычи в нём нет: этот
                  датасет прогнан с нулевым шансом дропа. Это не пропажа данных, а нулевая точка
                  замера ниже — с ней и надо сравнивать всё остальное. Шанс{' '}
                  {chance === undefined ? '—' : `${num(chance)}%`} на плитке выше пришёл из каталога
                  и из пресета <span className="mono">{preset?.file ?? '—'}</span>, а не из этого
                  датасета.
                </p>
              </Callout>
            ) : (
              <>
                <h3 style={{ fontSize: 'var(--fs-sm)', margin: '14px 0 8px' }}>
                  Что именно упало с трупов
                </h3>
                <table>
                  <thead>
                    <tr>
                      <th>Вид</th>
                      <th className="n">штук за выборку</th>
                      <th className="n">на уровень</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.byKind.map((entry) => (
                      <tr key={entry.kind}>
                        <td>{entry.label}</td>
                        <td className="n">{num(entry.amount)}</td>
                        <td className="n good">{num(entry.perRun, 2)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="dim">Трупов с добычей</td>
                      <td className="n dim">
                        {num(Math.round(summary.drops.mean * summary.dropRows))}
                      </td>
                      <td className="n dim">{num(summary.drops.mean, 2)}</td>
                    </tr>
                  </tbody>
                </table>
                <p className="dim" style={{ marginTop: 8 }}>
                  С трупов что-то упало у {percent(summary.observedDropShare)} убитых врагов. Это
                  наблюдение, а не настройка: шанс{' '}
                  {chance === undefined ? '—' : `${num(chance)}%`} разыгрывается на каждом враге, а
                  строка «пусто» отнимает часть удачных розыгрышей.
                </p>
              </>
            )}
          </>
        )}

        <h3 style={{ fontSize: 'var(--fs-sm)', margin: '18px 0 8px' }}>
          Замер: чем выше шанс, тем чаще ломается дефицит
        </h3>
        <table>
          <thead>
            <tr>
              <th className="n">шанс дропа</th>
              <th className="n">дефицит держится</th>
              <th className="n">ломается</th>
              <th style={{ width: '34%' }}>доля сломанных уровней</th>
            </tr>
          </thead>
          <tbody>
            {MEASURED_DEFICIT_CURVE.map((point) => {
              const current = measured !== null && measured.chancePercent === point.chancePercent;
              return (
                <tr key={point.chancePercent}>
                  <td className={current ? 'n mono good' : 'n mono'}>
                    {num(point.chancePercent)}%{current && <span className="faint"> ← сейчас</span>}
                  </td>
                  <td className="n">{percent(point.heldShare)}</td>
                  <td
                    className={
                      point.brokenShare === 0 ? 'n good' : point.brokenShare < 0.1 ? 'n warn' : 'n bad'
                    }
                  >
                    {percent(point.brokenShare)}
                  </td>
                  <td>
                    <Bar value={point.brokenShare} color="var(--alarm)" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="dim" style={{ marginBottom: 0, marginTop: 8 }}>
          Замер на {num(MEASURED_CURVE_RUNS)} прогонов базового пресета с таблицей «
          {MEASURED_CURVE_TABLE}» (CONTRACT.md, §1). Это <strong>измерение, а не формула</strong>:
          доля не выводится из шанса, потому что дефицит ломается только там, где запас патронов и
          без того лежал у нуля. Поменяется состав весов — числа станут неверны, и их надо
          перемерить ползунком «Шанс дропа с врага» на «Обзоре», а не пересчитать.
        </p>
      </Panel>
    </>
  );
}

function Bar({ value, color }: { value: number; color: string }) {
  const width = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0)) * 100;
  return (
    <div style={{ background: 'var(--track)', height: 8, borderRadius: 1, overflow: 'hidden' }}>
      <div style={{ background: color, width: `${width}%`, height: '100%', opacity: 0.75 }} />
    </div>
  );
}
